import { useCallback, useEffect, useRef } from 'react'
import { addDays, subDays, isBefore } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'
import { Button } from 'components/Button/Button'
import { useAsyncInitialization } from 'hooks/useAsyncInitialization'
import { medicareApiClient } from 'clients/medicareApiClient'
import { IMedicareCard, MailingAddress } from 'models/User'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { closeModal, getModalType, setModal } from 'redux/reducers/modal'
import { useDispatch, useSelector } from 'react-redux'
import { OrderMedicareCardModal } from 'containers/AccountSettings/components/OrderMedicareCardModal/OrderMedicareCardModal'
import { IconPrint } from 'components/Icon/IconPrint'
import { MedicareCardBack } from '../medicareCard/MedicareCardBack'
import { MedicareCardFront } from '../medicareCard/MedicareCardFront'
import { MedicareCardPrintable } from '../medicareCard'
import { useMountedState } from 'hooks/useMountedState'
import { IconOpenTab } from 'components/Icon/IconOpenTab'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { MyLink } from 'components/MyLink'
import { GenericError } from 'components/GenericError/GenericError'
import { formatMailingAddress } from '../HomeHeaderButtons'

export type GetMyMedicareCardPageContentProps = {
  lastCardRequestErrors?: string,
  lastCardRequestDateMs?: number,
  railroadRetirementBoardBeneficiary?: boolean,
  mailingAddress?: MailingAddress

}
export const GetMyMedicareCardPageContent = ({
  lastCardRequestErrors,
  lastCardRequestDateMs,
  railroadRetirementBoardBeneficiary,
  mailingAddress
}: GetMyMedicareCardPageContentProps) => {
  const { t } = useTranslation('getMyMedicareCardPage')
  const modalTypeOpen = useSelector(getModalType)

  const dispatch = useDispatch()
  const handleClose = () => dispatch(closeModal())
  const openOrderMedicareCardModal = () => {
    dispatch(setModal({ modalType: 'acct-order-medicare-card' }))
  }

  const [beneficiaryCardResponse, setBeneficiaryCardResponse] = useMountedState<{
    data?: IMedicareCard,
    status?: undefined | 'Ineligible Beneficiary'
  }>({})
  const [showPrint, setShowPrint] = useMountedState(false)

  const currentDate = new Date()
  const thirtyDaysAgo = subDays(currentDate, 30)
  const lastCardRequestDate = lastCardRequestDateMs ? new Date(Number(lastCardRequestDateMs)) : subDays(currentDate, 31)
  const thirtyDaysAfterLastOrdered = addDays(lastCardRequestDate, 30)
  const orderButtonDateIsValid = isBefore(lastCardRequestDate, thirtyDaysAgo)

  const OrderMedicareCardButtonSection = () => {
    const displayDates = {
      last: lastCardRequestDate.toLocaleDateString(),
      next: thirtyDaysAfterLastOrdered.toLocaleDateString()
    }

    const openIconTab = (
      <IconOpenTab
        className='my-u-mb--2 my-c-my-svg-icon--inline-block'
      />
    )

    if (railroadRetirementBoardBeneficiary) {
      return (
        <div className="my-u-max-width-md">
          <MyLink
            href="https://rrb.gov/Benefits/myRRB/ReplacementMedCard"
            target='_blank'
            rel="noopener noreferrer"
            siteActivityProps={{
              core: {
                component: 'Account Settings',
                elementId: 'accountsettings_ordercardrrb_click',
                coreClickData: {
                  linkText: t('rrb.link'),
                  linkUrl: 'https://rrb.gov/Benefits/myRRB/ReplacementMedCard'
                }
              }
            }}>{t('rrb.link')}{openIconTab}</MyLink>
          <h3 className='ds-text-heading--xl'>{t('rrb.title1')}</h3>
          <p>{t('rrb.para1')}</p>
          <p className='ds-text-heading--lg'>{t('rrb.mailTitle')}</p>
          <p className='my-u-mb--15'>{formatMailingAddress(mailingAddress)}</p>
          <ul className='my-u-ul-spacing my-u-mb--15'>
            <li>
              <MyLink
                href="https://rrb.gov/Benefits/myRRB"
                target='_blank'
                rel="noopener noreferrer"
                siteActivityProps={{
                  core: {
                    component: 'Account Settings',
                    elementId: 'accountsettings_ordercardrrbupdatenameoraddress_click',
                    coreClickData: {
                      linkText: t('rrb.ul.li1.linkText'),
                      linkUrl: 'https://rrb.gov/Benefits/myRRB'
                    }
                  }
                }}>{t('rrb.ul.li1.linkText')}{openIconTab}</MyLink>
              {t('rrb.ul.li1.text')}
            </li>
            <li>{t('rrb.ul.li2.text')}</li>
            <li>
              <MyLink
                href="https://rrb.gov/Field-Office-Locator"
                target='_blank'
                rel="noopener noreferrer"
                siteActivityProps={{
                  core: {
                    component: 'Account Settings',
                    elementId: 'accountsettings_ordercardrrbofficefinder_click',
                    coreClickData: {
                      linkText: t('rrb.ul.li3.linkText'),
                      linkUrl: 'https://rrb.gov/Field-Office-Locator'
                    }
                  }
                }}>{t('rrb.ul.li3.linkText')}{openIconTab}</MyLink>

              {t('rrb.ul.li3.text')}
            </li>
          </ul>
          <p>{t('rrb.para2')}</p>
        </div>
      )
    } else if (lastCardRequestErrors === 'ELMO offline') { // If elmo is offline the last requested date is probably undefined. In order to let users see elmo is offline we need to check this condition first.
      return (
        <Alert>
          <p className="ds-c-alert__text">{t('errors.elmoOffline')}</p>
        </Alert>
      )
    } else if (lastCardRequestErrors) {
      return <GenericError />
    } else if (!orderButtonDateIsValid) {
      return (
        <Alert hideIcon className='ds-u-measure--wide'>
          <p className='ds-u-margin-y--0'>{t('errors.dateAlert', { displayDates })}</p>
        </Alert>
      )
    } else {
      return (
        <Button
          className='ds-c-button ds-c-button--solid my-u-py--6'
          onClick={openOrderMedicareCardModal}
          siteActivityProps={{
            core: {
              component: 'Account Settings',
              elementId: 'accountsettings_ordercard_click',
              coreClickData: {
                linkText: t('printCardPage.btn-order')
              }
            }
          }}
        >
          {t('printCardPage.btn-order')}
        </Button>
      )
    }
  }

  const init = useCallback(
    async () => {
      try {
        const beneficiaryCardResponse: IMedicareCard =
          await medicareApiClient.asyncGetBeneficiaryMedicareCard()
        if (beneficiaryCardResponse) {
          setBeneficiaryCardResponse({ data: beneficiaryCardResponse })
        } else {
          throw new Error('Beneficiary Card data is undefined')
        }
      } catch (e: unknown) {
        if (e instanceof Error && e.message === 'Ineligible Beneficiary') {
          setBeneficiaryCardResponse({ status: 'Ineligible Beneficiary' })
        } else {
          throw e
        }
      }
    },
    // Need to keep dependencies blank on this or else an infinite loop can occur
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const loadingState = useAsyncInitialization(init)

  const componentRef = useRef(null)
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: t('heading')
  })
  const handlePrintClick = () => {
    setShowPrint(true)
  }

  // Ensures re-rendering before print dialog is opened
  useEffect(() => {
    if (showPrint) {
      handlePrint()
      setShowPrint(false)
    }
  }, [handlePrint, showPrint, setShowPrint])

  // const validMbi = Boolean(beneficiaryCardResponse.data && beneficiaryCardResponse.data.mbi)
  const responseData = beneficiaryCardResponse.data
  const isMedicareCardDataDefined = (data: IMedicareCard | undefined): data is IMedicareCard => typeof data === 'object'
  // use isMedicareCardDataDefined so ts knows that it is defined when mbi is true
  const validMbi = isMedicareCardDataDefined(responseData) && Boolean(responseData.mbi)

  return (
    <div className="my-u-mb--40">
       {modalTypeOpen === 'acct-order-medicare-card' && (
        <OrderMedicareCardModal onClose={handleClose} />
       )}
      <h2 className="ds-text-heading--2xl my-u-mb--2 my-u-lg-mb--6">
        {t('printCardPage.mainHeading')}
      </h2>
      <p>{t('printCardPage.mainParagraph')}</p>
      {loadingState === 'loading' && (
        <div>
          <MySpinner />
        </div>
      )}
      {loadingState === 'error' && <GenericError />}
      {loadingState === 'finished' && validMbi && (
        <div className='notranslate' translate='no'>
          <Button
            className="ds-c-button ds-c-button--solid my-u-py--6 my-u-mb--10 ds-u-display--flex ds-u-align-items--center"
            onClick={handlePrintClick}
            siteActivityProps={{
              core: {
                component: 'Account Settings',
                elementId: 'accountsettings_printcard_click',
                coreClickData: {
                  linkText: t('printCardPage.btn-print')
                }
              }
            }}
          >
            <IconPrint
              className="my-c-my-svg-icon--inline-block ds-u-color--white ds-u-valign--bottom ds-u-margin-right--1"
            />
            {' '}
            {t('printCardPage.btn-print')}
          </Button>
          <div className="my-c-medicare-card-wrapper">
            <MedicareCardFront
              beneficiaryCardData={responseData}
              partAActive={Boolean(responseData.partAEffectiveDate)}
              partBActive={Boolean(responseData.partBEffectiveDate)}
            />
            <MedicareCardBack />
          </div>
          {showPrint && (
            <div id="print-area" style={{ display: 'none' }}>
              <MedicareCardPrintable
                beneficiaryCardData={responseData}
                partAActive={Boolean(responseData.partAEffectiveDate)}
                partBActive={Boolean(responseData.partBEffectiveDate)}
                ref={componentRef}
              />
            </div>
          )}
        </div>
      )}
      {loadingState === 'finished' && !validMbi && (
        <Alert variation='warn' className="my-u-mt--12">
          <p className="ds-c-alert__text">{t(beneficiaryCardResponse.status === 'Ineligible Beneficiary' ? 'errors.cantGetInfo' : 'errors.noValidMbi')}</p>
        </Alert>
      )}
      {validMbi && (
        <>
        <h2 className="ds-text-heading--xl ds-u-margin-bottom--2 ds-u-margin-top--4">
          {t('printCardPage.orderHeading')}
        </h2>
        <OrderMedicareCardButtonSection/>
        </>
      )}
    </div>
  )
}
